import webApiClient from "./client";

/**
 * Event API Client
 *
 * Handles event discovery and iCal import operations:
 * - iCal import and status tracking
 * - Duplicate detection
 * - Event search and calendar queries
 *
 * NOTE: iCal import/update methods return the raw response object (not just
 * response.data) because updateIcalEvents can return HTTP 207 Multi-Status for
 * partial successes. Callers must inspect response.ok, response.status, and
 * response.data directly to tell partial success from full failure.
 */

export const eventApi = {
    // ====================
    // iCal Import
    // ====================

    /**
     * Import events from an iCal source
     * Returns the raw response. The import handler has no partial-success path —
     * it returns 400, 200 or 500 only (ical.controller.js:24-140), never 207.
     * @param {Array} events - Array of parsed iCal event objects to import
     * @returns {Promise<object>} Raw response — check response.ok and
     *   response.data.events
     */
    importIcalEvents: async events => {
        return webApiClient.post("/api/v2/event/ical/import", { events });
    },

    /**
     * Update previously imported iCal events
     * Returns the raw response — callers must handle HTTP 207 (partial success)
     * separately from HTTP 200 (full success) and HTTP 500 (full failure).
     * @param {Array} events - Array of iCal event objects to update
     * @returns {Promise<object>} Raw response — check response.ok, response.status,
     *   response.data.events, and response.data.error.details (the per-batch counts
     *   live under error.details, NOT data.details)
     */
    updateIcalEvents: async events => {
        return webApiClient.post("/api/v2/event/ical/update", { events });
    },

    /**
     * Get processing status for a set of imported events
     * @param {Array<string>} eventIds - Array of event IDs to check
     * @returns {Promise<Array>} Array of status objects per event
     * @throws {Error} If fetch fails
     */
    getUploadStatus: async eventIds => {
        const csv = eventIds.join(",");
        const response = await webApiClient.get(
            `/api/v2/event/ical/upload-status?eventIds=${encodeURIComponent(csv)}`
        );

        if (!response.ok || !response.data || !response.data.statuses) {
            throw new Error(response.data?.message || "Failed to fetch upload status");
        }

        return response.data.statuses;
    },

    // ====================
    // Duplicate Detection
    // ====================

    /**
     * Check a set of events for duplicates against existing events
     * @param {Array} eventChecks - Array of event objects to check
     * @returns {Promise<Array>} Array of duplicate result objects
     * @throws {Error} If check fails
     */
    checkDuplicates: async eventChecks => {
        const response = await webApiClient.post("/api/v2/event/check-duplicates", { eventChecks });

        if (!response.ok || !response.data || !response.data.duplicateResults) {
            throw new Error(response.data?.message || "Failed to check for duplicates");
        }

        return response.data.duplicateResults;
    },

    // ====================
    // Event Discovery
    // ====================

    /**
     * Search events and instructors
     * @param {object} params - Search parameters
     * @param {string} params.query - Search query string
     * @param {string} [params.searchType] - Search type filter
     * @param {string} [params.location] - Location filter
     * @returns {Promise<Array>} Array of matching event/instructor results
     * @throws {Error} If search fails
     */
    searchEvents: async params => {
        const queryParams = new URLSearchParams(params).toString();
        const response = await webApiClient.get(`/api/v2/event/search?${queryParams}`);

        if (!response.ok || !response.data || !response.data.results) {
            throw new Error(response.data?.message || "Failed to search events");
        }

        return response.data.results;
    },

    /**
     * Get events for a calendar date range
     * @param {string} startDate - ISO 8601 start date
     * @param {string} endDate - ISO 8601 end date
     * @returns {Promise<Array>} Array of event objects in the date range
     * @throws {Error} If fetch fails
     */
    getCalendarEvents: async (startDate, endDate) => {
        const response = await webApiClient.get(
            `/api/v2/event/calendar?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
        );

        if (!response.ok || !response.data || !response.data.events) {
            throw new Error(response.data?.message || "Failed to fetch calendar events");
        }

        return response.data.events;
    },
};
